import path from "path";
import { FileLogger, InterceptController } from "@dbcube/core";
import { createRequire } from 'module';
import type { TriggerConfig } from "../@types/Database";

/**
 * Runtime triggers: user-defined JS functions (from .trigger.cube files) that
 * run in the application process around each write. Because they execute in
 * the write-path (not as DB-side triggers), they behave identically across
 * MySQL, PostgreSQL, SQLite and MongoDB, and fire exactly once per operation
 * regardless of how many service instances share the database.
 */
export class Trigger {
    private triggers: TriggerConfig[];
    private databaseName: string;
    private instance: unknown;

    constructor(instance: unknown, databaseName: string, metadata: TriggerConfig[]) {
        this.triggers = metadata;
        this.databaseName = databaseName;
        this.instance = instance;
    }

    get(type: string): TriggerConfig | undefined {
        return this.triggers.find((tr) => tr.type === type);
    }

    async execute(type: string, row: Record<string, unknown>): Promise<InterceptController | null> {
        const trigger = this.triggers.find((tr) => tr.type === type);
        if (trigger) {
            const logFilePath = path.resolve(
                process.cwd(),
                'dbcube',
                'logs',
                'triggers',
                this.databaseName,
                `${trigger.table_ref}_${trigger.type}.log`
            );

            const interceptor = FileLogger.interceptConsole(logFilePath, {
                keepOriginal: false,
                useBuffer: true
            });
            const pathFile = path.resolve(process.cwd(), 'dbcube', 'triggers', `${trigger.database_ref}_${trigger.table_ref}_${trigger.type}.js`);
            // Use __filename for CJS, process.cwd() for ESM
            const requireUrl = typeof __filename !== 'undefined' ? __filename : process.cwd();
            const require = createRequire(requireUrl);
            // Clear require cache to ensure fresh load
            try {
                delete require.cache[require.resolve(pathFile)];
            } catch { /* bundled environments may not expose a resolvable cache */ }
            try {
                const triggerModule = require(pathFile);
                const dataProcess = triggerModule.default || triggerModule;
                await dataProcess({ db: this.instance, oldData: row, newData: row });
            } finally {
                // Sin esto, un trigger que lanza deja el console secuestrado
                // para SIEMPRE y el error se vuelve invisible para la app
                interceptor.restore();
            }
            return interceptor;
        }
        return null;
    }
}
